/**
 * TemplateBlocksPlayground
 *
 * Developer playground at /templates-v2-blocks for interactively testing
 * Phase 2 building block components (pickers, field filler, entity slots,
 * source badges, preview dialog).
 *
 * Layout: left column = template selection (search / category toggle),
 * right column = configuration flow (matter -> entity slots -> field filler -> preview).
 *
 * All state is local — no mutations, no side effects.
 */

import { ChevronDownIcon, ChevronRightIcon, EyeIcon } from '@heroicons/react/20/solid';
import { useCallback, useRef, useState } from 'react';

import { MatterSelectorCombobox } from '@/components/matter/MatterSelectorCombobox';
import type { ReadTemplateSummary } from '@/services/api/generated/templatesV2Service';

import { FieldSourceBadge } from './instantiate/FieldSourceBadge';
import { type EntityBindings, TemplateFieldFiller } from './instantiate/TemplateFieldFiller';
import { TemplateCategoryPicker } from './picker/TemplateCategoryPicker';
import { TemplateSearchPicker } from './picker/TemplateSearchPicker';
import { TemplatePreviewDialog } from './preview/TemplatePreviewDialog';

type PickerMode = 'search' | 'category';

export const TemplateBlocksPlayground = () => {
  // Shared selection state
  const [selectedTemplate, setSelectedTemplate] = useState<ReadTemplateSummary | null>(null);
  const [matterId, setMatterId] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);

  // Picker mode toggle
  const [pickerMode, setPickerMode] = useState<PickerMode>('search');

  // FieldFiller state — parent owns pending overrides + committed baseline
  const [pendingOverrides, setPendingOverrides] = useState<Record<string, string>>({});
  const committedRef = useRef<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);

  // Entity bindings state (coverage IDs, attorney ID, etc.)
  const [entityBindings, setEntityBindings] = useState<EntityBindings>({});

  // Source badges collapsible
  const [badgesOpen, setBadgesOpen] = useState(false);

  const handleSelect = useCallback((template: ReadTemplateSummary) => {
    setSelectedTemplate(template);
    setPendingOverrides({});
    committedRef.current = {};
    setIsDirty(false);
  }, []);

  const handleFieldChange = useCallback((key: string, value: string) => {
    setPendingOverrides((prev) => {
      const next = { ...prev, [key]: value };
      const baseline = committedRef.current;
      const allKeys = [...new Set([...Object.keys(next), ...Object.keys(baseline)])];
      setIsDirty(allKeys.some((k) => (next[k] ?? '') !== (baseline[k] ?? '')));
      return next;
    });
  }, []);

  const handleApply = useCallback(() => {
    const cleaned = Object.entries(pendingOverrides).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        if (value !== '') acc[key] = value;
        return acc;
      },
      {},
    );
    committedRef.current = cleaned;
    setPendingOverrides(cleaned);
    setIsDirty(false);
  }, [pendingOverrides]);

  const handleDiscard = useCallback(() => {
    setPendingOverrides(committedRef.current);
    setIsDirty(false);
  }, []);

  const selectedId = selectedTemplate ? (selectedTemplate.id as string) : null;

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 space-y-10">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
          Templates V2 — Block Playground
        </h1>
        <p className="mt-1 text-sm font-medium text-zinc-500">
          Interactive sandbox for Phase 2 building-block components.
        </p>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* ---- Left column: Template Selection ---- */}
        <div className="space-y-6">
          <Section
            title="Template Selection"
            description="Choose a template using search or category browsing"
          >
            {/* Mode toggle tabs */}
            <div className="flex gap-1 mb-4 rounded-lg bg-zinc-100 p-1 dark:bg-zinc-800">
              <button
                type="button"
                onClick={() => setPickerMode('search')}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                  pickerMode === 'search'
                    ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-100'
                    : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100'
                }`}
              >
                Search All
              </button>
              <button
                type="button"
                onClick={() => setPickerMode('category')}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${
                  pickerMode === 'category'
                    ? 'bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-zinc-100'
                    : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100'
                }`}
              >
                By Category
              </button>
            </div>

            {pickerMode === 'search' ? (
              <TemplateSearchPicker
                selectedId={selectedId}
                onSelect={handleSelect}
              />
            ) : (
              <TemplateCategoryPicker
                category="HIPAA_AUTHORIZATION"
                selectedId={selectedId}
                onSelect={handleSelect}
              />
            )}
          </Section>
        </div>

        {/* ---- Right column: Configuration Flow ---- */}
        <div className="space-y-6">
          {/* Selected template info + preview button */}
          {selectedTemplate && (
            <div className="flex items-center justify-between rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 dark:border-indigo-800 dark:bg-indigo-950">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                  Selected Template
                </p>
                <p className="truncate text-sm font-medium text-indigo-900 dark:text-indigo-100">
                  {selectedTemplate.display_name || selectedTemplate.name}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                className="ml-4 inline-flex shrink-0 items-center gap-2 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors dark:bg-indigo-500 dark:hover:bg-indigo-600"
              >
                <EyeIcon className="h-4 w-4" />
                Preview
              </button>
              <TemplatePreviewDialog
                templateId={selectedId!}
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                fieldValues={pendingOverrides}
              />
            </div>
          )}

          {/* Matter selector */}
          <Section
            title="Matter"
            description="Select a matter to load entity coverage slots"
          >
            <MatterSelectorCombobox
              value={matterId}
              onChange={setMatterId}
              placeholder="Search for a matter..."
              aria-label="Select matter for entity slots"
              includeAllOption={false}
            />
          </Section>

          {/* Field Filler (with integrated entity slots) */}
          <Section
            title="Field Filler"
            description="Edit field values with source badges and validation"
          >
            {selectedId ? (
              <TemplateFieldFiller
                templateId={selectedId}
                matterId={matterId || undefined}
                overrides={pendingOverrides}
                onFieldChange={handleFieldChange}
                isDirty={isDirty}
                onApply={handleApply}
                onDiscard={handleDiscard}
                entityBindings={entityBindings}
                onEntityBindingChange={setEntityBindings}
              />
            ) : (
              <EmptyHint>Select a template to see its fields.</EmptyHint>
            )}
          </Section>

          {/* Preview hint when no template selected */}
          {!selectedTemplate && (
            <EmptyHint>Select a template to enable preview.</EmptyHint>
          )}
        </div>
      </div>

      {/* ---- Collapsible Source Badges reference ---- */}
      <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900">
        <button
          type="button"
          onClick={() => setBadgesOpen((prev) => !prev)}
          className="flex w-full items-center justify-between px-4 py-3 text-left"
        >
          <div>
            <h2 className="text-base font-semibold text-zinc-800 dark:text-zinc-200">
              Component Reference: Source Badges
            </h2>
            <p className="text-sm font-medium text-zinc-500">
              FieldSourceBadge — all badge variants
            </p>
          </div>
          {badgesOpen ? (
            <ChevronDownIcon className="h-5 w-5 text-zinc-500" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-zinc-500" />
          )}
        </button>
        {badgesOpen && (
          <div className="border-t border-zinc-200 px-4 py-4 dark:border-zinc-700">
            <div className="flex flex-wrap items-center gap-4">
              <BadgeDemo label="Override">
                <FieldSourceBadge source="override" />
              </BadgeDemo>
              <BadgeDemo label="Context">
                <FieldSourceBadge source="context" />
              </BadgeDemo>
              <BadgeDemo label="Default">
                <FieldSourceBadge source="default" />
              </BadgeDemo>
              <BadgeDemo label="Missing">
                <FieldSourceBadge source="empty" isMissing />
              </BadgeDemo>
              <BadgeDemo label="Inline override">
                <FieldSourceBadge source="override" inline />
              </BadgeDemo>
              <BadgeDemo label="Inline missing">
                <FieldSourceBadge source="empty" isMissing inline />
              </BadgeDemo>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------- Helpers ---------- */

const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <section className="space-y-3">
    <div>
      <h2 className="text-base font-semibold text-zinc-800 dark:text-zinc-200">
        {title}
      </h2>
      <p className="text-sm font-medium text-zinc-500">{description}</p>
    </div>
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4 bg-white dark:bg-zinc-900">
      {children}
    </div>
  </section>
);

const BadgeDemo = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-center gap-1">
    <span className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
      {label}:
    </span>
    {children}
  </div>
);

const EmptyHint = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm font-medium text-zinc-500 italic py-4 text-center">
    {children}
  </p>
);
